import { Link, useParams } from "react-router-dom";
import { ArrowRight, MapPin, CheckCircle2 } from "lucide-react";
import Seo from "@/components/Seo";
import ServiceGrid from "@/components/ServiceGrid";
import Faq from "@/components/Faq";
import { MaskLines, Reveal, SectionHead } from "@/components/Reveal";
import { getCity, CITY_IMAGES, PHONE, PHONE_HREF, EMAIL_HREF } from "@/data";
import { getCyCity, CY_CITY_SLUGS, CY_CITY_UI as U } from "@/data/cy-cities";
import NotFound from "./NotFound";

const WelshCityPage = () => {
  const { slug } = useParams();
  const city = getCity(slug);
  const cy = getCyCity(slug);
  if (!city || !cy) return <NotFound />;
  const nearby = city.nearby.map(getCity).filter(Boolean).slice(0, 4);

  return (
    <div data-testid={`city-cy-page-${city.slug}`}>
      <Seo
        title={`Gwarchodwyr Diogelwch ${cy.name} | Gwasanaethau Trwyddedig SIA | Xtreme Protection`}
        description={`Gwarchodwyr diogelwch trwyddedig SIA ${cy.nameIn}, ${cy.county}. Gwarchod dynol, patrolau symudol, monitro CCTV, goruchwyliaeth drysau a diogelwch digwyddiadau. 24/7 — ffoniwch +44 2046 402840.`}
        lang="cy"
        path={`/cy/lleoliadau/${city.slug}`}
        altEn={`/locations/${city.slug}`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: "Gwasanaethau Gwarchod Diogelwch",
          provider: { "@type": "LocalBusiness", name: "Xtreme Protection — Wales Security Services", telephone: "+44 2046 402840", email: "admin@xtremeprotection.uk" },
          areaServed: { "@type": "City", name: `${cy.name}, ${cy.county}, Cymru` },
        }}
      />

      <section className="xp-noise relative overflow-hidden border-b border-[#1C2027] pb-16 pt-32">
        <div className="absolute inset-0">
          <img src={CITY_IMAGES[city.slug]?.url} alt={CITY_IMAGES[city.slug]?.alt || cy.name} className="h-full w-full object-cover opacity-25" loading="lazy" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0C10] via-[#0A0C10]/80 to-[#0A0C10]/40" />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6">
          <p className="eyebrow flex items-center gap-3">
            <span className="h-px w-10 bg-[#F5A800]" />
            {U.eyebrowPrefix} // {cy.county} // {cy.regionCy}
          </p>
          <MaskLines
            className="font-condensed mt-6 text-5xl font-bold uppercase leading-[0.9] tracking-tight text-white sm:text-6xl lg:text-7xl"
            lines={[U.h1Top, cy.name]}
          />
          <Reveal delay={0.35}>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-[#C8CCD2] sm:text-lg">{cy.tag}. Swyddogion trwyddedig SIA, patrolau wedi'u gwirio a chefnogaeth rheoli 24/7 {cy.nameIn} ac ar draws {cy.county}.</p>
          </Reveal>
          <Reveal delay={0.45}>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href={PHONE_HREF} data-testid="city-cy-call-btn" className="group flex items-center gap-2 bg-[#F5A800] px-6 py-3.5 text-sm font-bold uppercase tracking-[0.14em] text-[#0A0C10] transition-colors duration-300 hover:bg-white">
                {U.ctaCall} {PHONE}
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </a>
              <Link to="/services" data-testid="city-cy-services-btn" className="flex items-center gap-2 border border-white/25 px-6 py-3.5 text-sm font-bold uppercase tracking-[0.14em] text-white transition-colors duration-300 hover:border-[#F5A800] hover:text-[#F5A800]">
                {U.ctaServices}
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:py-20">
        <div className="grid gap-12 lg:grid-cols-[2fr_1fr]">
          <div>
            <SectionHead index={U.introIndex} eyebrow={`${cy.name} ${U.introEyebrowSuffix}`} title={`${U.introTitleIn} ${cy.nameIn}`} testid="city-cy-intro-heading" />
            <Reveal delay={0.1}>
              <p className="mt-6 text-base leading-relaxed text-[#9BA1AB]">{cy.intro}</p>
            </Reveal>
            <h3 className="font-condensed mt-10 text-2xl font-bold uppercase tracking-tight text-white">{U.riskTitle} {cy.nameIn}</h3>
            <Reveal delay={0.1}>
              <p className="mt-4 text-base leading-relaxed text-[#9BA1AB]">{cy.risk}</p>
            </Reveal>
            <h3 className="font-condensed mt-10 text-2xl font-bold uppercase tracking-tight text-white">{U.deployTitle} {cy.county}</h3>
            <Reveal delay={0.1}>
              <p className="mt-4 text-base leading-relaxed text-[#9BA1AB]">{cy.deploy}</p>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <div className="border border-[#1C2027] bg-[#10131A] p-6">
              <h3 className="font-condensed text-xl font-bold uppercase tracking-wide text-white">{U.areasTitle} {cy.nameIn}</h3>
              <ul className="mt-4 space-y-2.5" data-testid="city-cy-areas-list">
                {city.areas.map((a) => (
                  <li key={a} className="flex items-center gap-2.5 text-sm text-[#9BA1AB]">
                    <CheckCircle2 className="h-4 w-4 shrink-0 text-[#F5A800]" />
                    {a}
                  </li>
                ))}
              </ul>
              <div className="mt-6 border-t border-[#1C2027] pt-5">
                <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/50">{U.areasNote} {cy.county}</p>
                <p className="mt-2 text-sm text-[#9BA1AB]">{U.areasBody}</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-[#1C2027] bg-[#0D1015] py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <SectionHead index={U.servicesIndex} eyebrow={U.servicesEyebrow} title={`${U.servicesTitleIn} ${cy.nameIn}`} testid="city-cy-services-heading" />
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-[#9BA1AB]">{U.servicesSub}</p>
          <div className="mt-10">
            <ServiceGrid />
          </div>
        </div>
      </section>

      {nearby.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
          <SectionHead index={U.nearbyIndex} eyebrow={U.nearbyEyebrow} title={`${U.nearbyTitle} ${cy.name}`} testid="city-cy-nearby-heading" />
          <div className="mt-8 grid gap-px border border-[#1C2027] bg-[#1C2027] sm:grid-cols-2 lg:grid-cols-4">
            {nearby.map((n, i) => (
              <Reveal key={n.slug} delay={i * 0.07} y={14} className="h-full">
                <Link
                  to={CY_CITY_SLUGS.has(n.slug) ? `/cy/lleoliadau/${n.slug}` : `/locations/${n.slug}`}
                  data-testid={`nearby-cy-city-${n.slug}`}
                  className="group flex h-full items-center justify-between bg-[#10131A] p-5 transition-colors duration-300 hover:bg-[#161A22]"
                >
                  <div>
                    <p className="font-condensed text-lg font-bold uppercase tracking-wide text-white transition-colors duration-300 group-hover:text-[#F5A800]">{n.name}</p>
                    <p className="text-[11px] uppercase tracking-[0.2em] text-white/40">{n.county}</p>
                  </div>
                  <MapPin className="h-4 w-4 text-[#F5A800]/60 transition-transform duration-300 group-hover:scale-125" />
                </Link>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      <section className="border-t border-[#1C2027] bg-[#0D1015] py-16 lg:py-20">
        <div className="mx-auto max-w-5xl px-4 sm:px-6">
          <SectionHead index={U.faqIndex} eyebrow={U.faqEyebrow} title={`${cy.name} ${U.faqTitleSuffix}`} testid="city-cy-faq-heading" />
          <div className="mt-10">
            <Faq items={cy.faqs} idPrefix="city-cy-faq" />
          </div>
        </div>
      </section>

      <section className="bg-[#F5A800] py-12" data-testid="city-cy-cta-band">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-6 px-4 sm:px-6 lg:flex-row lg:items-center">
          <h2 className="font-condensed text-3xl font-bold uppercase leading-none text-[#0A0C10] sm:text-4xl">{U.ctaTitleIn} {cy.nameIn}</h2>
          <div className="flex flex-wrap gap-3">
            <a href={PHONE_HREF} data-testid="city-cy-cta-call" className="bg-[#0A0C10] px-6 py-3.5 text-sm font-bold uppercase tracking-[0.14em] text-[#F5A800] transition-colors duration-300 hover:bg-[#1A1E26]">
              {U.ctaCallBtn} {PHONE}
            </a>
            <a href={EMAIL_HREF} data-testid="city-cy-cta-email" className="border-2 border-[#0A0C10] px-6 py-3.5 text-sm font-bold uppercase tracking-[0.14em] text-[#0A0C10] transition-colors duration-300 hover:bg-[#0A0C10] hover:text-[#F5A800]">
              {U.ctaEmailBtn}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WelshCityPage;
